import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randNumber, randChanceBoolean, rand } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import { pageable, filterable } from './utils.js'
import {
  getMondayOfCurrentWeek,
  getTodayDateTimeISO,
  getDateTimeTimestamp,
  getDateTimeISOFromTimestamp,
  getPeriod,
} from '@/utils/date/getter.js'
import { addToDate, adjustDateTime } from '@/utils/date/calculation.js'
import { formatDate } from '@/utils/date/conversion.js'
import { safeMathRandom } from '@/utils/number.js'

export type UserSchedulingData = SchemaAttributes<'UserScheduling'>

type ShiftType = {
  background_color: string
  text_color: string
  name: string
}

const SHIFT_TYPES: ShiftType[] = [
  { background_color: '#E3F2FD', text_color: '#1976D2', name: 'Morning' },
  { background_color: '#FFF3E0', text_color: '#F57C00', name: 'Afternoon' },
  { background_color: '#F3E5F5', text_color: '#7B1FA2', name: 'Evening' },
  { background_color: '#E8F5E9', text_color: '#388E3C', name: 'Night' },
  { background_color: '#FCE4EC', text_color: '#C2185B', name: 'Break' },
]

export const model = (Model as ModelDefinition<UserSchedulingData>).extend({
  user: belongsTo('user'),
  department: belongsTo('department'),
})

export const factory = Factory.extend<UserSchedulingData>({
  user_id() {
    return null
  },
  start() {
    // Get Monday of current week using date utilities
    const mondayISO = getMondayOfCurrentWeek()

    // Random day within the week (0-6 days from Monday) using falso
    const dayOffset = randNumber({ min: 0, max: 6 })
    const randomDate = addToDate(mondayISO, { days: dayOffset })

    // Random time between 6 AM and 10 PM using falso
    const hours = randNumber({ min: 6, max: 22 })
    const minutes = rand([0, 15, 30, 45])

    // Adjust date time using date utilities
    const adjustedDate = adjustDateTime(randomDate, {
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0,
    })

    return typeof adjustedDate === 'string'
      ? adjustedDate
      : adjustedDate.toISOString()
  },
  end() {
    // Shifts are typically 4-12 hours long using falso
    const duration = randNumber({ min: 4, max: 12 })
    const startValue = this.start as string
    const endDate = addToDate(startValue, { hours: duration })
    return typeof endDate === 'string' ? endDate : endDate.toISOString()
  },
  start_date() {
    return formatDate(this.start as string, 'yyyy-MM-dd')
  },
  end_date() {
    return formatDate(this.end as string, 'yyyy-MM-dd')
  },
  shift_type() {
    return randChanceBoolean({ chanceTrue: 0.8 }) ? rand(SHIFT_TYPES) : null
  },
  available_for_booking() {
    // Most shifts are available for booking, but breaks are not
    const shiftType = this.shift_type as ShiftType | null
    if (shiftType?.name === 'Break') {
      return false
    }
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  afterCreate(userScheduling, server) {
    if (!userScheduling.user) {
      let users = server.schema.all('user')

      if (users.length === 0) {
        server.createList('user', 15)
        users = server.schema.all('user')
      }

      const randomUser =
        users.models[Math.floor(safeMathRandom() * users.models.length)]
      userScheduling.update({
        user: randomUser,
        user_id: Number(randomUser.id),
      })
    }
  },
})

export function routes(server: MockServer) {
  server.get('/shifts', function (this: MirageServer, schema, request) {
    const shifts = schema.all('userScheduling')

    // Filter by date range (events that overlap with the date range)
    const filtered = filterable(shifts, request, {
      'department.id': (item, value) => {
        const department = item.department as { id: string } | null
        return department?.id === value
      },
      start_date: (item, value) => {
        const startDate = item.attrs.start_date as string | null
        if (!startDate || !value) {
          return true
        }
        return startDate >= value
      },
      end_date: (item, value) => {
        const endDate = item.attrs.end_date as string | null
        if (!endDate || !value) {
          return true
        }
        return endDate <= value
      },
      'user.in': (item, value) => {
        const userId = item.attrs.user_id as number | null
        if (!userId) {
          return false
        }
        const userIds = Array.isArray(value) ? value : [value]
        return userIds.some((id) => Number(userId) === Number(id))
      },
      user: (item, value) => {
        const userId = item.attrs.user_id as number | null
        return userId !== null && Number(userId) === Number(value)
      },
    })

    // Additional date range filtering: events that overlap with the date range
    const startDateFilter = request.queryParams['filter[start_date]'] as
      | string
      | undefined
    const endDateFilter = request.queryParams['filter[end_date]'] as
      | string
      | undefined

    let dateFiltered = filtered
    if (startDateFilter && endDateFilter) {
      dateFiltered = filtered.filter((shift) => {
        const shiftStart = shift.attrs.start as string | null
        const shiftEnd = shift.attrs.end as string | null
        if (!shiftStart || !shiftEnd) {
          return false
        }

        // Compare ISO strings directly (they're lexicographically sortable)
        return shiftStart <= endDateFilter && shiftEnd >= startDateFilter
      })
    }

    // Paginate
    const { data, meta } = pageable(dateFiltered, request)

    // Construct response
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })
}

export function seeds(server: MockServer) {
  // Create shifts for the next 2 months
  const users = server.schema.all('user')
  const departments = server.schema.all('department')

  if (users.length === 0 || departments.length === 0) {
    return
  }

  const today = getTodayDateTimeISO()
  const period = getPeriod(today, 'month')
  const fromTimestamp = getDateTimeTimestamp(period.from)
  const toTimestamp = getDateTimeTimestamp(period.to)

  // Create 50-100 shifts distributed across the month
  const shiftCount = randNumber({ min: 50, max: 100 })

  for (let i = 0; i < shiftCount; i++) {
    const startTimestamp =
      fromTimestamp + safeMathRandom() * (toTimestamp - fromTimestamp)
    const startDateISO = getDateTimeISOFromTimestamp(startTimestamp)
    const hours = randNumber({ min: 6, max: 22 })
    const minutes = rand([0, 15, 30, 45])
    const adjustedStartDate = adjustDateTime(startDateISO, {
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0,
    })
    const startISO =
      typeof adjustedStartDate === 'string'
        ? adjustedStartDate
        : (adjustedStartDate as Date).toISOString()

    const duration = randNumber({ min: 4, max: 12 })
    const endDate = addToDate(startISO, { hours: duration })

    const randomUser =
      users.models[Math.floor(safeMathRandom() * users.models.length)]
    const randomDepartment =
      departments.models[
        Math.floor(safeMathRandom() * departments.models.length)
      ]

    const endISO = typeof endDate === 'string' ? endDate : endDate.toISOString()

    server.create('userScheduling', {
      start: startISO,
      end: endISO,
      start_date: formatDate(startISO, 'yyyy-MM-dd'),
      end_date: formatDate(endISO, 'yyyy-MM-dd'),
      user: randomUser,
      user_id: Number(randomUser.id),
      department: randomDepartment,
      available_for_booking: randChanceBoolean({ chanceTrue: 0.9 }),
      shift_type: randChanceBoolean({ chanceTrue: 0.8 })
        ? rand(SHIFT_TYPES)
        : null,
    })
  }
}
